import { useEffect, useMemo, useRef, useState } from 'react';
import { ACTIVE_COMPANY_KEY, api } from '../api.js';
import { PALETTE } from '../palette.js';
import { Button, Card, Pill } from '../components/primitives.jsx';
import ProtectedPageLoader from '../components/ProtectedPageLoader.jsx';
import {
  ConfirmationDialog,
  useConfirmationDialog,
} from '../components/ConfirmationDialog.jsx';
import {
  IconCheck,
  IconChevron,
  IconExternal,
  IconInfo,
  IconLink,
  IconLightning,
  IconMap,
  IconRefresh,
  IconSettings,
  IconTarget,
  IconUnlink,
  IconUsers,
  IconVideo,
} from '../components/icons.jsx';
import { safeFailureMessage } from '../safe-failure.js';

const ADMIN_ROLES = new Set(['owner', 'admin']);
const REQUIRED_PROVIDERS = ['meta'];
const DEFAULT_CAMPAIGN_DEFAULTS = {
  dailyBudget: 10,
  radiusKm: 50,
  city: '',
  ageMin: 25,
  ageMax: 65,
  currency: 'USD',
};
function tenantHeaders(companyId) {
  return companyId ? { headers: { 'x-company-id': companyId } } : undefined;
}

function getActiveCompanyId(session) {
  const memberships = session.user?.memberships || [];
  return session.user?.activeCompany?.id || memberships[0]?.companyId || '';
}

function titleCase(value) {
  return String(value || '')
    .split(/[_\s-]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ') || 'Unknown';
}

function formatDateTime(value) {
  if (!value) return 'Not checked';
  return new Date(value).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function memberName(member) {
  return member.displayName || member.email || 'Pending member';
}

function persistActiveCompanyId(companyId) {
  try {
    if (companyId) localStorage.setItem(ACTIVE_COMPANY_KEY, companyId);
  } catch {}
}

function primaryCredential(credentials, provider) {
  const candidates = (credentials || []).filter((credential) => credential.provider === provider);
  return candidates.find((credential) => credential.status === 'active')
    || candidates.find((credential) => credential.status === 'needs_reauth')
    || candidates[0]
    || null;
}

function readinessByProvider(readiness) {
  const map = new Map();
  for (const item of readiness?.providers || []) {
    if (item?.provider) map.set(item.provider, item);
  }
  return map;
}

export default function SaasSettings({ session, navigate }) {
  const sessionCompanyId = getActiveCompanyId(session);
  const [selectedCompanyId, setSelectedCompanyId] = useState(sessionCompanyId);
  const [context, setContext] = useState(null);
  const [policy, setPolicy] = useState(null);
  const [credentials, setCredentials] = useState([]);
  const [readiness, setReadiness] = useState(null);
  const [checklist, setChecklist] = useState(null);
  const [settingsDefaults, setSettingsDefaults] = useState(null);
  const [limitState, setLimitState] = useState(null);
  const [members, setMembers] = useState([]);
  const [status, setStatus] = useState(() => (sessionCompanyId ? 'loading' : 'idle'));
  const [error, setError] = useState(null);
  const [refreshNonce, setRefreshNonce] = useState(0);
  const loadRequestRef = useRef(0);

  const memberships = context?.memberships?.length
    ? context.memberships
    : session.user?.memberships || [];
  const activeCompany =
    context?.activeCompany ||
    memberships.find((membership) => membership.companyId === selectedCompanyId)?.company ||
    session.user?.activeCompany ||
    null;
  const role = context?.role || session.user?.role || null;
  const actions = policy?.actions || [];
  const canReadCredentials = actions.includes('credentials.read_masked');
  const canManageCredentials = actions.includes('credentials.manage');
  const canReadMembers = actions.includes('members.read');
  const canInviteMembers = actions.includes('members.invite');
  const canUpdateSettings = actions.includes('company.update_settings');

  function clearTenantSettings() {
    setContext(null);
    setPolicy(null);
    setCredentials([]);
    setReadiness(null);
    setChecklist(null);
    setSettingsDefaults(null);
    setLimitState(null);
    setMembers([]);
  }

  async function loadSettings(companyId = selectedCompanyId) {
    const requestId = loadRequestRef.current + 1;
    loadRequestRef.current = requestId;
    clearTenantSettings();
    if (!companyId) {
      setStatus('idle');
      setError(null);
      return;
    }
    setStatus('loading');
    setError(null);
    try {
      const headers = tenantHeaders(companyId);
      const [nextContext, nextPolicy] = await Promise.all([
        api.get('/api/saas/context', headers),
        api.get('/api/saas/policy', headers),
      ]);
      const nextActions = nextPolicy.actions || [];
      const credentialReadable = nextActions.includes('credentials.read_masked');
      const memberReadable = nextActions.includes('members.read');
      const companyReadable = nextActions.includes('company.read');
      const usageReadable = nextActions.includes('usage.read');
      const optionalRequests = await Promise.all([
        credentialReadable
          ? api.get('/api/saas/credentials', headers)
          : Promise.resolve({ credentials: [] }),
        credentialReadable
          ? api.get(`/api/saas/integrations/readiness?requiredProviders=${REQUIRED_PROVIDERS.join(',')}`, headers)
          : Promise.resolve({ readiness: null }),
        credentialReadable
          ? api.get('/api/saas/onboarding/readiness', headers)
          : Promise.resolve({ checklist: null }),
        memberReadable
          ? api.get('/api/saas/members', headers)
          : Promise.resolve({ members: [] }),
        companyReadable
          ? api.get('/api/saas/settings/defaults', headers)
          : Promise.resolve(null),
        usageReadable
          ? api.get('/api/saas/limits', headers)
          : Promise.resolve({ limitState: null }),
      ]);

      if (requestId !== loadRequestRef.current) return;
      setContext(nextContext);
      setPolicy(nextPolicy);
      setCredentials(optionalRequests[0].credentials || []);
      setReadiness(optionalRequests[1].readiness || null);
      setChecklist(optionalRequests[2].checklist || null);
      setMembers(optionalRequests[3].members || []);
      setSettingsDefaults(optionalRequests[4] || null);
      setLimitState(optionalRequests[5].limitState || null);
      session.setActiveTenantContext?.(nextContext);
      persistActiveCompanyId(companyId);
      setStatus('ready');
    } catch (err) {
      if (requestId !== loadRequestRef.current) return;
      clearTenantSettings();
      setError(err);
      setStatus('error');
    }
  }

  useEffect(() => {
    loadSettings(selectedCompanyId);
  }, [selectedCompanyId, refreshNonce]);

  const readinessMap = useMemo(() => readinessByProvider(readiness), [readiness]);
  const metaCredential = primaryCredential(credentials, 'meta');
  const metaReadiness = readinessMap.get('meta');
  const blockerCount = canReadCredentials
    ? checklist?.counts?.blockers || readiness?.blockers?.length || 0
    : null;
  const campaignCount = checklist?.counts?.campaigns || 0;
  const primaryBrand = checklist?.primaryBrand || null;
  const settingsLoaded = Boolean(context && policy);
  const showInitialSettingsLoading = status === 'loading' && Boolean(selectedCompanyId) && !settingsLoaded;

  if (showInitialSettingsLoading) {
    return (
      <PageShell>
        <Header
          activeCompany={activeCompany}
          role={role}
          status={status}
          onRefresh={() => setRefreshNonce((value) => value + 1)}
        />
        <ProtectedPageLoader />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <Header
        activeCompany={activeCompany}
        role={role}
        status={status}
        onRefresh={() => setRefreshNonce((value) => value + 1)}
      />

      {status === 'error' && (
        <div style={{ marginBottom: 16 }}>
          <Notice tone="warn" title="Settings unavailable" body={safeFailureMessage(error, 'settings')} />
        </div>
      )}

      <section style={summaryGridStyle}>
        <Metric
          label="Meta connection"
          value={canReadCredentials ? metaConnectionLabel(metaCredential, metaReadiness) : 'Restricted'}
          tone={!canReadCredentials ? 'warn' : metaReadiness?.ready ? 'live' : 'warn'}
        />
        <Metric label="Members visible" value={canReadMembers ? String(members.length) : 'Restricted'} tone={canReadMembers ? 'neutral' : 'warn'} />
        <Metric
          label="Launch blockers"
          value={blockerCount === null ? 'Restricted' : String(blockerCount)}
          tone={blockerCount === null ? 'warn' : blockerCount ? 'warn' : 'live'}
        />
      </section>

      <section style={topGridStyle}>
        <TenantCard
          memberships={memberships}
          selectedCompanyId={selectedCompanyId}
          activeCompany={activeCompany}
          role={role}
          onCompanyChange={setSelectedCompanyId}
        />
        <AccessCard
          role={role}
          canUpdateSettings={canUpdateSettings}
          canReadMembers={canReadMembers}
          canInviteMembers={canInviteMembers}
        />
      </section>

      <SectionHeader
        title="Meta Ads connection"
        body="Connect the Meta account this company owns. Beyond Leads keeps provider credentials and diagnostics out of tenant settings."
        status={readiness?.ready ? 'Ready' : 'Attention'}
        tone={readiness?.ready ? 'live' : 'warn'}
      />

      {!canReadCredentials ? (
        <Notice
          tone="warn"
          title="Meta Ads settings are restricted"
          body="Viewer roles cannot read credential metadata. Ask an owner or admin to manage Meta Ads settings."
        />
      ) : (
        <MetaConnectionCard
          key={`${selectedCompanyId}:${metaCredential?.id || 'disconnected'}`}
          companyId={selectedCompanyId}
          credential={metaCredential}
          readiness={metaReadiness}
          canManage={canManageCredentials}
          onChanged={() => setRefreshNonce((value) => value + 1)}
        />
      )}

      <SectionHeader
        title="Campaign defaults"
        body="Legacy ad targeting and video defaults preserved for SaaS campaign launches."
        status={canUpdateSettings ? 'Editable' : 'Read-only'}
        tone={canUpdateSettings ? 'accent' : 'neutral'}
      />

      <DefaultsSettingsPanel
        companyId={selectedCompanyId}
        payload={settingsDefaults}
        canManage={canUpdateSettings}
        onLoaded={setSettingsDefaults}
        onSaved={(nextPayload) => {
          setSettingsDefaults(nextPayload);
          setRefreshNonce((value) => value + 1);
        }}
      />

      <section style={lowerGridStyle}>
        <MembersPanel
          members={members}
          role={role}
          canReadMembers={canReadMembers}
          canInviteMembers={canInviteMembers}
          navigate={navigate}
        />
        <VideoProfilePanel
          primaryBrand={primaryBrand}
          videoSection={checklist?.sections?.find((section) => section.id === 'video-voice')}
          canManage={canManageCredentials || ADMIN_ROLES.has(role)}
          navigate={navigate}
        />
        <LimitsPanel
          checklist={checklist}
          campaignCount={campaignCount}
          limitState={limitState}
          canReadUsage={actions.includes('usage.read')}
        />
      </section>
    </PageShell>
  );
}

function operationKey(prefix) {
  const suffix = globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(16).slice(2)}`;
  return `${prefix}:${suffix}`;
}

function domainTone(status) {
  if (status === 'active') return 'live';
  if (status === 'failed') return 'warn';
  if (status === 'disabled') return 'neutral';
  return 'accent';
}

// Preserved historical component contract. It is not mounted by the current
// settings screen because custom lead-site hosting is outside the product.
export function CustomerDomainsPanel({ companyId, canManage }) {
  const [funnels, setFunnels] = useState([]);
  const [funnelId, setFunnelId] = useState('');
  const [domains, setDomains] = useState([]);
  const [newHostname, setNewHostname] = useState('');
  const [busy, setBusy] = useState('');
  const [toast, setToast] = useState(null);
  const loadRequestRef = useRef(0);
  const contextSequenceRef = useRef(0);
  const companyRef = useRef(companyId);
  const funnelRef = useRef(funnelId);
  const domainsRef = useRef(domains);
  const busyRef = useRef(busy);
  const canManageRef = useRef(canManage);
  const confirmationDialog = useConfirmationDialog();
  companyRef.current = companyId;
  funnelRef.current = funnelId;
  domainsRef.current = domains;
  busyRef.current = busy;
  canManageRef.current = canManage;

  async function load(nextFunnelId = funnelId, reloadFunnels = false) {
    if (!companyId || !canManage) return;
    const requestedCompanyId = companyId;
    const requestId = ++loadRequestRef.current;
    const isCurrent = () => requestId === loadRequestRef.current && companyRef.current === requestedCompanyId;
    setBusy('load');
    try {
      let available = reloadFunnels ? [] : funnels;
      if (!available.length) {
        const result = await api.get('/api/saas/funnel-domain-options', tenantHeaders(companyId));
        if (!isCurrent()) return;
        available = result.funnels || [];
        setFunnels(available);
      }
      const selected = nextFunnelId || available[0]?.id || '';
      if (!isCurrent()) return;
      setFunnelId(selected);
      if (!selected) { setDomains([]); return; }
      const result = await api.get(`/api/saas/funnels/${encodeURIComponent(selected)}/domains`, tenantHeaders(companyId));
      if (!isCurrent()) return;
      setDomains(result.domains || []);
      setToast(null);
    } catch (err) {
      if (isCurrent()) setToast({ tone: 'err', message: safeFailureMessage(err, 'settings') });
    } finally { if (isCurrent()) setBusy(''); }
  }

  useEffect(() => {
    contextSequenceRef.current += 1;
    loadRequestRef.current += 1;
    busyRef.current = '';
    setBusy('');
    setFunnels([]); setFunnelId(''); setDomains([]); setToast(null);
    if (canManage) load('', true);
  }, [companyId, canManage]);

  async function addDomain(event) {
    event.preventDefault();
    if (!funnelId || !newHostname.trim()) return;
    const requestedCompanyId = companyId;
    setBusy('add'); setToast({ tone: 'info', message: 'Registering the hostname and checking DNS…' });
    try {
      const result = await api.post(`/api/saas/funnels/${encodeURIComponent(funnelId)}/domains`, { hostname: newHostname }, {
        headers: { 'x-company-id': companyId, 'Idempotency-Key': operationKey('domain-request') },
      });
      if (companyRef.current !== requestedCompanyId) return;
      setNewHostname(''); await load(funnelId);
      if (companyRef.current !== requestedCompanyId) return;
      setToast(result.cacheInvalidation?.status === 'pending'
        ? { tone: 'info', message: 'The domain is verified; secure routing is still propagating. Check again shortly.' }
        : result.domain?.status === 'failed'
        ? { tone: 'warn', message: 'The hostname is saved, but registration needs attention. Retry verification when the provider is available.' }
        : { tone: 'ok', message: 'Domain registered. Add the DNS record below, then check verification.' });
    } catch (err) { if (companyRef.current === requestedCompanyId) setToast({ tone: 'err', message: safeFailureMessage(err, 'domain') }); }
    finally { if (companyRef.current === requestedCompanyId) setBusy(''); }
  }

  async function verify(domain) {
    const requestedCompanyId = companyId;
    setBusy(domain.id); setToast({ tone: 'info', message: 'Checking DNS and certificate status…' });
    try {
      const result = await api.post(`/api/saas/funnels/${encodeURIComponent(funnelId)}/domains/${encodeURIComponent(domain.id)}/verification-checks`, {}, {
        headers: { 'x-company-id': companyId, 'Idempotency-Key': operationKey('domain-verify') },
      });
      if (companyRef.current !== requestedCompanyId) return;
      await load(funnelId);
      if (companyRef.current !== requestedCompanyId) return;
      setToast(result.cacheInvalidation?.status === 'pending'
        ? { tone: 'info', message: 'Domain verified. Secure routing is still propagating; check again shortly.' }
        : result.domain?.status === 'active'
        ? { tone: 'ok', message: 'Domain verified and ready.' }
        : result.domain?.status === 'failed'
          ? { tone: 'warn', message: 'Verification needs attention. Confirm DNS or retry when the provider is available.' }
          : { tone: 'info', message: 'Verification is still pending. Confirm the DNS record and check again.' });
    } catch (err) { if (companyRef.current === requestedCompanyId) setToast({ tone: 'err', message: safeFailureMessage(err, 'domain') }); }
    finally { if (companyRef.current === requestedCompanyId) setBusy(''); }
  }

  async function makePrimary(domain) {
    const requestedCompanyId = companyId;
    setBusy(domain.id);
    try {
      const result = await api.patch(`/api/saas/funnels/${encodeURIComponent(funnelId)}/domains/${encodeURIComponent(domain.id)}`, { primary: true }, {
        headers: { 'x-company-id': companyId, 'Idempotency-Key': operationKey('domain-primary'), 'If-Match': `"${domain.stateVersion}"` },
      });
      if (companyRef.current !== requestedCompanyId) return;
      await load(funnelId);
      if (companyRef.current !== requestedCompanyId) return;
      setToast(result.cacheInvalidation?.status === 'pending'
        ? { tone: 'info', message: 'Primary domain updated. Secure routing is still propagating.' }
        : { tone: 'ok', message: 'Primary funnel domain updated.' });
    } catch (err) { if (companyRef.current === requestedCompanyId) setToast({ tone: 'err', message: safeFailureMessage(err, 'domain') }); }
    finally { if (companyRef.current === requestedCompanyId) setBusy(''); }
  }

  async function remove(domain) {
    const requestedCompanyId = companyId;
    const requestedFunnelId = funnelId;
    const requestedDomainId = domain.id;
    const requestedContextSequence = contextSequenceRef.current;
    if (!requestedCompanyId || !requestedFunnelId || !requestedDomainId || busyRef.current || !canManage) return;
    const approved = await confirmationDialog.request({
      eyebrow: 'Customer domain',
      title: `Remove ${domain.hostname}?`,
      description: `BeyondLeads will stop routing this funnel through ${domain.hostname}. The automatic platform URL remains available while routing caches clear.`,
      confirmLabel: 'Remove domain',
      cancelLabel: 'Keep domain',
      tone: 'danger',
    });
    if (!approved) return;
    const targetStillCurrent = domainsRef.current.some((candidate) => (
      candidate?.id === requestedDomainId
      && candidate?.hostname === domain.hostname
      && candidate?.stateVersion === domain.stateVersion
      && candidate?.status === domain.status
    ));
    if (
      companyRef.current !== requestedCompanyId
      || funnelRef.current !== requestedFunnelId
      || contextSequenceRef.current !== requestedContextSequence
      || !canManageRef.current
      || busyRef.current
      || !targetStillCurrent
    ) return;
    busyRef.current = requestedDomainId;
    setBusy(domain.id);
    try {
      const result = await api.del(`/api/saas/funnels/${encodeURIComponent(requestedFunnelId)}/domains/${encodeURIComponent(requestedDomainId)}`, {
        headers: { 'x-company-id': requestedCompanyId, 'Idempotency-Key': operationKey('domain-remove'), 'If-Match': `"${domain.stateVersion}"` },
        body: { reason: 'Removed by company administrator' },
      });
      if (
        companyRef.current !== requestedCompanyId
        || funnelRef.current !== requestedFunnelId
        || contextSequenceRef.current !== requestedContextSequence
      ) return;
      await load(requestedFunnelId);
      if (
        companyRef.current !== requestedCompanyId
        || funnelRef.current !== requestedFunnelId
        || contextSequenceRef.current !== requestedContextSequence
      ) return;
      setToast(result.cacheInvalidation?.status === 'pending'
        ? { tone: 'info', message: 'Customer domain removed. The platform fallback is active while routing caches clear.' }
        : { tone: 'ok', message: 'Customer domain removed. The platform URL is the active fallback.' });
    } catch (err) {
      if (
        companyRef.current === requestedCompanyId
        && funnelRef.current === requestedFunnelId
        && contextSequenceRef.current === requestedContextSequence
      ) {
        setToast({ tone: 'err', message: safeFailureMessage(err, 'domain') });
      }
    }
    finally {
      if (
        companyRef.current === requestedCompanyId
        && funnelRef.current === requestedFunnelId
        && contextSequenceRef.current === requestedContextSequence
        && busyRef.current === requestedDomainId
      ) {
        busyRef.current = '';
        setBusy('');
      }
    }
  }

  if (!canManage) return <Notice title="Owner or admin managed" body="Customer domain records and verification details are available only to company owners and admins." />;

  const visible = domains.filter((domain) => domain.status !== 'disabled');
  return (
    <>
    <Card style={{ ...panelStyle, padding: 0, overflow: 'hidden', marginBottom: 26 }}>
      <div style={providerHeaderStyle}>
        <div style={{ minWidth: 0, flex: '1 1 280px' }}>
          <FieldLabel>Published funnel</FieldLabel>
          <select value={funnelId} onChange={(event) => load(event.target.value)} style={{ ...selectStyle, marginTop: 8 }} disabled={busy === 'load'}>
            {!funnels.length && <option value="">No funnels available</option>}
            {funnels.map((funnel) => <option key={funnel.id} value={funnel.id}>{funnel.name || funnel.slug}</option>)}
          </select>
        </div>
        <form onSubmit={addDomain} style={{ display: 'flex', alignItems: 'end', gap: 8, flex: '2 1 360px', flexWrap: 'wrap' }}>
          <label style={{ flex: '1 1 240px' }}><FieldLabel>Customer hostname</FieldLabel><input value={newHostname} onChange={(event) => setNewHostname(event.target.value)} placeholder="offers.example.com" style={{ ...inputStyle, marginTop: 8 }} /></label>
          <Button type="submit" variant="accent" size="sm" disabled={!funnelId || !newHostname.trim() || Boolean(busy)}><IconLink size={13} /> Add domain</Button>
        </form>
      </div>
      <div style={providerBodyStyle}>
        {toast && <div style={{ marginBottom: 12 }}><Toast toast={toast} /></div>}
        {!funnelId ? <Notice compact title="No funnel selected" body="Publish a funnel before connecting a customer domain." /> : visible.length === 0 ? (
          <div style={emptyMaskedStyle}>No domain mappings yet. The automatic Beyond Leads URL is created when this funnel is published.</div>
        ) : (
          <div style={{ display: 'grid', gap: 10 }}>
            {visible.map((domain) => (
              <div key={domain.id} style={{ border: `1px solid ${PALETTE.lineSoft}`, borderRadius: 8, padding: 14, display: 'grid', gap: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap' }}>
                  <div style={{ minWidth: 0 }}><div style={{ fontWeight: 650, overflowWrap: 'anywhere' }}>{domain.hostname}</div><div style={panelTextStyle}>{domain.domainType === 'platform' ? 'Automatic platform URL' : 'Customer-owned domain'}</div></div>
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}><Pill tone={domainTone(domain.status)}>{titleCase(domain.status)}</Pill>{domain.isPrimary && <Pill tone="live">Primary</Pill>}</div>
                </div>
                {domain.domainType === 'customer' && domain.dnsRecords?.length > 0 && domain.status !== 'active' && (
                  <div style={connectionIdentityGridStyle}>{domain.dnsRecords.map((record, index) => <div key={`${record.type}:${index}`} style={connectionIdentityItemStyle}><span style={metricLabelStyle}>{record.type} · {record.name}</span><span style={{ ...connectionIdentityValueStyle, whiteSpace: 'normal', overflowWrap: 'anywhere' }}>{record.value}</span></div>)}</div>
                )}
                {domain.lastFailureCode && <Notice compact tone="warn" title="Needs attention" body={domain.lastFailureCode === 'dns_misconfigured'
                  ? 'Update the DNS record shown above, then check again.'
                  : domain.lastFailureCode === 'tls_provisioning_pending'
                    ? 'The certificate and secure renderer route are still provisioning. Check again shortly.'
                    : 'Domain verification is not complete yet. Confirm DNS and retry.'} />}
                {domain.domainType === 'customer' && <div style={actionsStyle}>
                  {domain.status !== 'active' && <Button type="button" variant="ghost" size="sm" disabled={Boolean(busy)} onClick={() => verify(domain)}><IconRefresh size={13} /> Check verification</Button>}
                  {domain.status === 'active' && !domain.isPrimary && <Button type="button" variant="accent" size="sm" disabled={Boolean(busy)} onClick={() => makePrimary(domain)}>Make primary</Button>}
                  <Button type="button" variant="ghost" size="sm" disabled={Boolean(busy)} onClick={() => remove(domain)}><IconUnlink size={13} /> Remove</Button>
                </div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </Card>
    <ConfirmationDialog {...confirmationDialog.dialogProps} />
    </>
  );
}

function Header({ activeCompany, role, status, onRefresh }) {
  return (
    <header style={headerStyle}>
      <div>
        <div style={eyebrowStyle}>Settings</div>
        <h1 style={titleStyle}>Tenant controls</h1>
        <div style={subtitleStyle}>
          {activeCompany?.name || 'Active company'} · {titleCase(role)}
        </div>
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <Pill tone={ADMIN_ROLES.has(role) ? 'accent' : 'neutral'}>{titleCase(role)}</Pill>
        <Button variant="ghost" size="sm" onClick={onRefresh} disabled={status === 'loading'}>
          <IconRefresh size={14} />
          Refresh
        </Button>
      </div>
    </header>
  );
}

function TenantCard({ memberships, selectedCompanyId, activeCompany, role, onCompanyChange }) {
  return (
    <Card style={panelStyle}>
      <SectionTitle icon={<IconUsers size={16} />} title="Company" />
      <div style={{ display: 'grid', gap: 12 }}>
        <FieldLabel>Active company</FieldLabel>
        <select
          value={selectedCompanyId}
          onChange={(event) => {
            persistActiveCompanyId(event.target.value);
            onCompanyChange(event.target.value);
          }}
          disabled={memberships.length <= 1}
          style={selectStyle}
        >
          {memberships.map((membership) => (
            <option key={membership.id || membership.companyId} value={membership.companyId}>
              {membership.company?.name || membership.companyId}
            </option>
          ))}
        </select>
        <div style={contextGridStyle}>
          <ContextStat label="Tenant" value={activeCompany?.slug || 'not set'} />
          <ContextStat label="Role" value={titleCase(role)} />
          <ContextStat label="Companies" value={String(memberships.length || 0)} />
        </div>
      </div>
    </Card>
  );
}

function AccessCard({
  canUpdateSettings,
  canReadMembers,
  canInviteMembers,
}) {
  return (
    <Card style={panelStyle}>
      <SectionTitle icon={<IconCheck size={16} />} title="Role policy" />
      <div style={{ display: 'grid', gap: 9 }}>
        <AccessRow label="View company members" enabled={canReadMembers} />
        <AccessRow label="Invite users" enabled={canInviteMembers} />
        <AccessRow label="Update tenant settings" enabled={canUpdateSettings} />
      </div>
    </Card>
  );
}

function metaConnectionLabel(credential, readiness) {
  if (!credential) return 'Not connected';
  if (readiness?.ready || (!readiness && credential.status === 'active')) return 'Connected';
  if (credential.status === 'needs_reauth') return 'Reconnect';
  return 'Needs attention';
}

function connectionIdentityRows(maskedSummary = {}) {
  const rows = [
    ['Meta profile', maskedSummary.metaUserName, maskedSummary.metaUserId],
    ['Business', maskedSummary.businessName, maskedSummary.businessId],
    ['Ad account', maskedSummary.accountName, maskedSummary.adAccountId],
    ['Facebook Page', maskedSummary.pageName, maskedSummary.pageId],
    ['Instagram', maskedSummary.instagramUsername, maskedSummary.instagramUserId],
    ['Pixel', maskedSummary.pixelName, maskedSummary.pixelId],
  ];
  return rows
    .filter(([, name, id]) => name || id)
    .map(([label, name, id]) => ({ label, value: [name, id].filter(Boolean).join(' · ') }));
}

export function MetaConnectionCard({ companyId, credential, readiness, canManage, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null);
  const companyRef = useRef(companyId);
  const credentialIdRef = useRef(credential?.id || '');
  const busyRef = useRef(busy);
  const canManageRef = useRef(canManage);
  const operationSequenceRef = useRef(0);
  const confirmationDialog = useConfirmationDialog();
  companyRef.current = companyId;
  credentialIdRef.current = credential?.id || '';
  busyRef.current = busy;
  canManageRef.current = canManage;
  const identityRows = connectionIdentityRows(credential?.maskedSummary);
  const connectionState = metaConnectionLabel(credential, readiness);
  const connected = connectionState === 'Connected';
  const showBody = Boolean(identityRows.length || credential || toast || !canManage);

  useEffect(() => {
    operationSequenceRef.current += 1;
    busyRef.current = false;
    setBusy(false);
    setToast(null);
  }, [companyId, canManage]);

  async function connectMeta() {
    const requestedCompanyId = companyId;
    const requestedCredentialId = credential?.id || '';
    if (!requestedCompanyId || !canManageRef.current || busyRef.current) return;
    const operationId = ++operationSequenceRef.current;
    busyRef.current = true;
    setBusy(true);
    setToast({ tone: 'info', message: 'Opening Meta sign-in…' });
    try {
      const redirectUri = new URL('/meta/callback', window.location.origin).toString();
      sessionStorage.setItem('lpam:meta-company-id', requestedCompanyId);
      const result = await api.get(
        `/api/saas/integrations/meta/connect-url?redirectUri=${encodeURIComponent(redirectUri)}`,
        tenantHeaders(requestedCompanyId),
      );
      if (
        companyRef.current !== requestedCompanyId
        || credentialIdRef.current !== requestedCredentialId
        || !canManageRef.current
        || operationSequenceRef.current !== operationId
      ) {
        if (
          sessionStorage.getItem('lpam:meta-company-id') === requestedCompanyId
          && (
            operationSequenceRef.current === operationId
            || companyRef.current !== requestedCompanyId
          )
        ) {
          sessionStorage.removeItem('lpam:meta-company-id');
        }
        if (
          companyRef.current === requestedCompanyId
          && operationSequenceRef.current === operationId
        ) {
          busyRef.current = false;
          setBusy(false);
        }
        return;
      }
      window.location.assign(result.url);
    } catch (err) {
      if (
        sessionStorage.getItem('lpam:meta-company-id') === requestedCompanyId
        && (
          operationSequenceRef.current === operationId
          || companyRef.current !== requestedCompanyId
        )
      ) {
        sessionStorage.removeItem('lpam:meta-company-id');
      }
      if (
        companyRef.current === requestedCompanyId
        && credentialIdRef.current === requestedCredentialId
        && canManageRef.current
        && operationSequenceRef.current === operationId
      ) {
        busyRef.current = false;
        setBusy(false);
        setToast({ tone: 'err', message: safeFailureMessage(err, 'integration') });
      }
    }
  }

  async function disconnectMeta() {
    const requestedCompanyId = companyId;
    const requestedCredentialId = credential?.id;
    const requestedOperationSequence = operationSequenceRef.current;
    if (!canManage || !requestedCredentialId || busyRef.current) return;
    const approved = await confirmationDialog.request({
      eyebrow: 'Meta Ads connection',
      title: 'Disconnect this company from Meta Ads?',
      description: 'BeyondLeads will remove this company’s stored Meta connection. Campaign publishing and Meta lead access will pause until an owner or admin reconnects Meta. Existing Meta assets are not deleted.',
      confirmLabel: 'Disconnect Meta',
      cancelLabel: 'Keep connected',
      tone: 'danger',
    });
    if (!approved) return;
    if (
      companyRef.current !== requestedCompanyId
      || credentialIdRef.current !== requestedCredentialId
      || !canManageRef.current
      || busyRef.current
      || operationSequenceRef.current !== requestedOperationSequence
    ) return;
    const operationId = ++operationSequenceRef.current;
    busyRef.current = true;
    setBusy(true);
    setToast({ tone: 'info', message: 'Disconnecting Meta…' });
    try {
      await api.del(`/api/saas/credentials/${encodeURIComponent(requestedCredentialId)}`, tenantHeaders(requestedCompanyId));
      if (
        companyRef.current !== requestedCompanyId
        || credentialIdRef.current !== requestedCredentialId
        || operationSequenceRef.current !== operationId
      ) return;
      setToast({ tone: 'ok', message: 'Meta disconnected.' });
      onChanged();
    } catch (err) {
      if (
        companyRef.current === requestedCompanyId
        && credentialIdRef.current === requestedCredentialId
        && operationSequenceRef.current === operationId
      ) {
        setToast({ tone: 'err', message: safeFailureMessage(err, 'integration') });
      }
    } finally {
      if (
        companyRef.current === requestedCompanyId
        && operationSequenceRef.current === operationId
        && busyRef.current
      ) {
        busyRef.current = false;
        setBusy(false);
      }
    }
  }

  return (
    <>
    <Card style={{ ...panelStyle, padding: 0, overflow: 'hidden' }}>
      <div style={providerHeaderStyle}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
            <h3 style={providerTitleStyle}>Meta Ads</h3>
            <Pill tone={connected ? 'live' : credential ? 'warn' : 'neutral'}>{connectionState}</Pill>
          </div>
          <p style={panelTextStyle}>
            {connected
              ? 'Beyond Leads can publish approved campaigns to this company’s Meta account.'
              : credential
                ? 'Reconnect Meta to restore this company’s publishing access.'
                : 'Connect the company’s Meta account to publish approved Facebook and Instagram campaigns.'}
          </p>
        </div>
        {canManage && (
          <Button
            type="button"
            variant={credential ? 'ghost' : 'accent'}
            size="sm"
            disabled={busy}
            onClick={credential ? disconnectMeta : connectMeta}
            style={{ whiteSpace: 'nowrap', flex: '0 0 auto' }}
          >
            {credential ? <IconUnlink size={13} /> : <IconLink size={13} />}
            {busy ? 'Please wait' : credential ? 'Disconnect' : 'Connect Meta'}
          </Button>
        )}
      </div>

      {showBody && (
        <div style={providerBodyStyle}>
          {identityRows.length > 0 && (
            <div style={connectionIdentityGridStyle}>
              {identityRows.map((row) => (
                <div key={row.label} style={connectionIdentityItemStyle}>
                  <span style={metricLabelStyle}>{row.label}</span>
                  <span style={connectionIdentityValueStyle}>{row.value}</span>
                </div>
              ))}
            </div>
          )}
          {credential && identityRows.length === 0 && (
            <div style={emptyMaskedStyle}>
              Connection identity will appear after Meta access is refreshed.
            </div>
          )}

          {credential && (
            <div style={{ marginTop: 12, color: PALETTE.ink3, fontSize: 11.5 }}>
              Last checked {formatDateTime(readiness?.lastTestedAt || credential.lastTestedAt)}
            </div>
          )}
          {!canManage && (
            <div style={{ marginTop: 12 }}>
              <Notice compact title="Owner or admin managed" body="Ask a company owner or admin to change the Meta connection." />
            </div>
          )}
          {toast && <div style={{ marginTop: 12 }}><Toast toast={toast} /></div>}
          {credential && !connected && canManage && (
            <div style={{ marginTop: 12 }}>
              <Button type="button" variant="accent" size="sm" disabled={busy} onClick={connectMeta}>
                <IconExternal size={13} />
                Reconnect Meta
              </Button>
            </div>
          )}
        </div>
      )}
    </Card>
    <ConfirmationDialog {...confirmationDialog.dialogProps} />
    </>
  );
}

export function DefaultsSettingsPanel({ companyId, payload, canManage, onLoaded, onSaved }) {
  const [brandId, setBrandId] = useState(payload?.brand?.id || '');
  const [campaignDefaults, setCampaignDefaults] = useState(DEFAULT_CAMPAIGN_DEFAULTS);
  const [toast, setToast] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setBrandId(payload?.brand?.id || '');
    setCampaignDefaults({
      ...DEFAULT_CAMPAIGN_DEFAULTS,
      ...(payload?.defaults?.campaignDefaults || {}),
    });
    setToast(null);
  }, [companyId, payload]);

  async function loadBrandDefaults(nextBrandId) {
    setBrandId(nextBrandId);
    if (!nextBrandId || !companyId) return;
    setToast({ tone: 'info', message: 'Loading brand defaults…' });
    try {
      const result = await api.get(
        `/api/saas/settings/defaults?brandId=${encodeURIComponent(nextBrandId)}`,
        tenantHeaders(companyId),
      );
      onLoaded(result);
      setToast(null);
    } catch (err) {
      setToast({ tone: 'err', message: safeFailureMessage(err, 'settings') });
    }
  }

  function setCampaignField(field, value) {
    setCampaignDefaults((current) => ({ ...current, [field]: value }));
  }

  async function saveDefaults(event) {
    event.preventDefault();
    if (!canManage || saving || !brandId) return;
    setSaving(true);
    setToast({ tone: 'info', message: 'Saving defaults…' });
    try {
      const body = {
        brandId,
        campaignDefaults: {
          dailyBudget: Number(campaignDefaults.dailyBudget) || DEFAULT_CAMPAIGN_DEFAULTS.dailyBudget,
          radiusKm: Number(campaignDefaults.radiusKm) || DEFAULT_CAMPAIGN_DEFAULTS.radiusKm,
          city: String(campaignDefaults.city || '').trim(),
          ageMin: Number(campaignDefaults.ageMin) || DEFAULT_CAMPAIGN_DEFAULTS.ageMin,
          ageMax: Number(campaignDefaults.ageMax) || DEFAULT_CAMPAIGN_DEFAULTS.ageMax,
          currency: String(campaignDefaults.currency || DEFAULT_CAMPAIGN_DEFAULTS.currency).trim().toUpperCase(),
        },
      };
      const result = await api.patch('/api/saas/settings/defaults', body, tenantHeaders(companyId));
      onSaved(result);
      setToast({ tone: 'ok', message: 'Saved. Campaign launches will use these SaaS defaults.' });
    } catch (err) {
      setToast({ tone: 'err', message: safeFailureMessage(err, 'settings') });
    } finally {
      setSaving(false);
    }
  }

  if (!payload) {
    return (
      <Card style={panelStyle}>
        <Notice compact title="Loading defaults" body="Campaign and media defaults load with the active company context." />
      </Card>
    );
  }

  const brands = payload.brands || [];
  const selectedBrand = brands.find((brand) => brand.id === brandId) || payload.brand;

  if (!selectedBrand) {
    return (
      <Card style={panelStyle}>
        <Notice
          compact
          tone="warn"
          title="No brand available"
          body="Create a tenant-owned brand before configuring campaign and media defaults."
        />
      </Card>
    );
  }

  return (
    <Card style={panelStyle}>
      <form onSubmit={saveDefaults} style={{ display: 'grid', gap: 18 }}>
        <div style={panelHeaderRowStyle}>
          <div style={{ minWidth: 0 }}>
            <SectionTitle icon={<IconSettings size={16} />} title="Launch defaults" compact />
            <p style={{ ...panelTextStyle, marginTop: 8 }}>
              Business defaults are stored on the selected SaaS brand and copied into new campaign records.
            </p>
          </div>
          <Pill tone={canManage ? 'accent' : 'neutral'}>{canManage ? 'Owner/Admin' : 'Read-only'}</Pill>
        </div>

        <label style={{ display: 'grid', gap: 6 }}>
          <FieldLabel>Brand</FieldLabel>
          <select
            value={brandId}
            onChange={(event) => loadBrandDefaults(event.target.value)}
            disabled={brands.length <= 1 || saving}
            style={selectStyle}
          >
            {brands.map((brand) => (
              <option key={brand.id} value={brand.id}>
                {brand.name || brand.slug}
              </option>
            ))}
          </select>
        </label>

        {!canManage && (
          <Notice
            compact
            title="Restricted"
            body="This role can view defaults but cannot change launch settings."
          />
        )}

        <div style={defaultsGridStyle}>
          <div style={defaultsGroupStyle}>
            <SectionTitle icon={<IconTarget size={16} />} title="Ad targeting" compact />
            <div style={{ ...formGridStyle, marginTop: 12 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <FieldLabel>Daily budget</FieldLabel>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={campaignDefaults.dailyBudget ?? ''}
                  onChange={(event) => setCampaignField('dailyBudget', event.target.value)}
                  disabled={!canManage || saving}
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <FieldLabel>Radius km</FieldLabel>
                <input
                  type="number"
                  min="1"
                  step="1"
                  value={campaignDefaults.radiusKm ?? ''}
                  onChange={(event) => setCampaignField('radiusKm', event.target.value)}
                  disabled={!canManage || saving}
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <FieldLabel>Default city</FieldLabel>
                <input
                  value={campaignDefaults.city || ''}
                  onChange={(event) => setCampaignField('city', event.target.value)}
                  disabled={!canManage || saving}
                  placeholder="Ottawa, ON, Canada"
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <FieldLabel>Age min</FieldLabel>
                <input
                  type="number"
                  min="18"
                  max="65"
                  value={campaignDefaults.ageMin ?? ''}
                  onChange={(event) => setCampaignField('ageMin', event.target.value)}
                  disabled={!canManage || saving}
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <FieldLabel>Age max</FieldLabel>
                <input
                  type="number"
                  min="18"
                  max="65"
                  value={campaignDefaults.ageMax ?? ''}
                  onChange={(event) => setCampaignField('ageMax', event.target.value)}
                  disabled={!canManage || saving}
                  style={inputStyle}
                />
              </label>
              <label style={{ display: 'grid', gap: 6 }}>
                <FieldLabel>Currency</FieldLabel>
                <input
                  value={campaignDefaults.currency || ''}
                  onChange={(event) => setCampaignField('currency', event.target.value)}
                  disabled={!canManage || saving}
                  style={inputStyle}
                />
              </label>
            </div>
          </div>

          <div style={defaultsGroupStyle}>
            <SectionTitle icon={<IconVideo size={16} />} title="Video media" compact />
            <div style={{ display: 'grid', gap: 12, marginTop: 12 }}>
              <div style={managedMediaStyle}>
                <div>
                  <strong style={{ display: 'block', color: PALETTE.ink, fontSize: 13.5 }}>
                    Managed by Beyond Leads
                  </strong>
                  <span style={{ color: PALETTE.ink3, fontSize: 12.5, lineHeight: 1.5 }}>
                    Voice and campaign-ready footage are selected automatically for each project type.
                  </span>
                </div>
                <Pill tone={payload?.defaults?.media?.ready ? 'live' : 'warn'}>
                  {payload?.defaults?.media?.ready ? 'Included' : 'Support needed'}
                </Pill>
              </div>
              {!payload?.defaults?.media?.ready && (
                <Notice
                  compact
                  tone="warn"
                  title="Media service unavailable"
                  body="Contact Beyond Leads support. No company integration changes are required."
                />
              )}
            </div>
          </div>
        </div>

        <div style={actionsStyle}>
          <Button type="submit" variant="accent" size="sm" disabled={!canManage || saving || !brandId}>
            <IconCheck size={13} />
            Save Defaults
          </Button>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: PALETTE.ink3, fontSize: 12.5 }}>
            <IconMap size={13} />
            {selectedBrand.slug}
          </div>
          {toast && <Toast toast={toast} />}
        </div>
      </form>
    </Card>
  );
}

function MembersPanel({ members, canReadMembers, canInviteMembers, navigate }) {
  return (
    <Card style={panelStyle}>
      <div style={panelHeaderRowStyle}>
        <SectionTitle icon={<IconUsers size={16} />} title="Users and roles" compact />
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('company')}
          disabled={!canReadMembers && !canInviteMembers}
        >
          Manage
          <IconChevron size={13} />
        </Button>
      </div>
      {!canReadMembers ? (
        <Notice
          compact
          tone="warn"
          title="Member roster is restricted"
          body="Owner and admin roles manage company users and invitations."
        />
      ) : members.length === 0 ? (
        <Notice compact title="No members visible" body="No active or invited members were returned for this company." />
      ) : (
        <div style={{ display: 'grid', gap: 8 }}>
          {members.slice(0, 5).map((member) => (
            <div key={member.id} style={memberRowStyle}>
              <div style={{ minWidth: 0 }}>
                <div style={{ color: PALETTE.ink, fontSize: 13, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {memberName(member)}
                </div>
                <div style={{ color: PALETTE.ink3, fontSize: 11.5 }}>{member.status || 'active'}</div>
              </div>
              <Pill tone={member.role === 'owner' ? 'accent' : 'neutral'}>{titleCase(member.role)}</Pill>
            </div>
          ))}
          {members.length > 5 && <div style={panelTextStyle}>{members.length - 5} more members in company admin.</div>}
        </div>
      )}
    </Card>
  );
}

function VideoProfilePanel({ primaryBrand, videoSection, canManage, navigate }) {
  const items = videoSection?.items || [];
  return (
    <Card style={panelStyle}>
      <div style={panelHeaderRowStyle}>
        <SectionTitle icon={<IconLightning size={16} />} title="Video profile" compact />
        <Button variant="ghost" size="sm" onClick={() => navigate('onboarding')} disabled={!canManage}>
          Review
          <IconChevron size={13} />
        </Button>
      </div>
      <div style={{ display: 'grid', gap: 10 }}>
        <ContextStat label="Launch brand" value={primaryBrand?.name || 'Not configured'} />
        {items.length ? items.map((item) => (
          <AccessRow
            key={item.id}
            label={item.label}
            enabled={item.status === 'ready'}
            pending={item.status === 'attention'}
            detail={item.message}
          />
        )) : (
          <Notice compact title="Video readiness unavailable" body="Video and voice readiness appears after launch checklist data is available." />
        )}
      </div>
    </Card>
  );
}

function limitByKey(limitState, key) {
  return limitState?.limits?.find((limit) => limit.key === key) || null;
}

function formatLimitUsage(limit) {
  if (!limit) return 'Unknown';
  return `${limit.used ?? 0}/${limit.limit ?? 0}`;
}

function LimitsPanel({ checklist, campaignCount, limitState, canReadUsage }) {
  const hasChecklist = Boolean(checklist);
  const workflowLimit = limitByKey(limitState, 'workflow_concurrency');
  const campaignLimit = limitByKey(limitState, 'monthly_campaigns');
  const videoLimit = limitByKey(limitState, 'monthly_video_generations');
  const limits = [
    {
      label: 'Workflow concurrency',
      value: canReadUsage ? formatLimitUsage(workflowLimit) : 'Restricted',
      detail: workflowLimit?.blocked
        ? 'Another workflow execution is already queued or running for this company.'
        : 'Dispatched queued/running attempts and running workflows for this company.',
    },
    {
      label: 'Campaign count',
      value: canReadUsage ? formatLimitUsage(campaignLimit) : hasChecklist ? `${campaignCount} current` : 'Restricted',
      detail: canReadUsage
        ? 'Tenant campaigns created in the current UTC month.'
        : hasChecklist
          ? 'Current tenant campaign count from readiness data.'
          : 'Campaign count requires launch-readiness access.',
    },
    {
      label: 'Video generation',
      value: canReadUsage ? formatLimitUsage(videoLimit) : 'Restricted',
      detail: videoLimit?.blocked
        ? 'Monthly video generation quota has been reached.'
        : 'Video render usage events recorded in the current UTC month.',
    },
    {
      label: 'Launch readiness',
      value: hasChecklist ? checklist?.ready ? 'Ready' : 'Blocked' : 'Restricted',
      detail: hasChecklist
        ? `${checklist?.counts?.blockers || 0} blocking items, ${checklist?.counts?.optional || 0} optional items.`
        : 'Launch readiness is not visible to this role.',
    },
  ];

  return (
    <Card style={panelStyle}>
      <SectionTitle icon={<IconInfo size={16} />} title="Operating limits" />
      <div style={{ display: 'grid', gap: 10 }}>
        {limits.map((limit) => (
          <div key={limit.label} style={limitRowStyle}>
            <div>
              <div style={{ color: PALETTE.ink, fontSize: 13, fontWeight: 600 }}>{limit.label}</div>
              <div style={{ color: PALETTE.ink3, fontSize: 11.5, marginTop: 3 }}>{limit.detail}</div>
            </div>
            <div style={{ color: PALETTE.ink2, fontSize: 12.5, fontWeight: 600, textAlign: 'right' }}>{limit.value}</div>
          </div>
        ))}
        {canReadUsage && limitState?.blockers?.length > 0 && (
          <Notice
            compact
            tone="warn"
            title="Launch limited"
            body={limitState.blockers.map((blocker) => blocker.message).join(' ')}
          />
        )}
      </div>
    </Card>
  );
}

function SectionHeader({ title, body, status, tone }) {
  return (
    <div style={sectionHeaderStyle}>
      <div>
        <h2 style={sectionTitleStyle}>{title}</h2>
        <p style={sectionBodyStyle}>{body}</p>
      </div>
      <Pill tone={tone}>{status}</Pill>
    </div>
  );
}

function Metric({ label, value, tone }) {
  const color = tone === 'warn'
    ? PALETTE.warn
    : tone === 'live'
      ? PALETTE.success
      : tone === 'accent'
        ? PALETTE.accent
        : PALETTE.ink;
  return (
    <div style={metricStyle}>
      <div style={metricLabelStyle}>{label}</div>
      <div style={{ color, fontSize: 24, fontWeight: 650, lineHeight: 1.05 }}>{value}</div>
    </div>
  );
}

function SectionTitle({ icon, title, compact = false }) {
  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      color: PALETTE.ink,
      fontSize: 14,
      fontWeight: 650,
      marginBottom: compact ? 0 : 16,
    }}>
      <span style={{ color: PALETTE.accent }}>{icon}</span>
      {title}
    </div>
  );
}

function AccessRow({ label, enabled, pending = false, detail }) {
  const tone = enabled ? 'live' : pending ? 'accent' : 'warn';
  const text = enabled ? 'Enabled' : pending ? 'Attention' : 'Restricted';
  return (
    <div style={{ display: 'grid', gap: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{
          width: 22,
          height: 22,
          borderRadius: 999,
          display: 'grid',
          placeItems: 'center',
          background: enabled ? PALETTE.successSoft : PALETTE.surfaceAlt,
          color: enabled ? PALETTE.success : PALETTE.ink3,
          flex: '0 0 auto',
        }}>
          {enabled ? <IconCheck size={13} /> : <IconInfo size={13} />}
        </span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13, color: PALETTE.ink2 }}>{label}</span>
        <Pill tone={tone} style={{ fontSize: 10, padding: '3px 8px' }}>{text}</Pill>
      </div>
      {detail && <div style={{ marginLeft: 32, color: PALETTE.ink3, fontSize: 11.5, lineHeight: 1.35 }}>{detail}</div>}
    </div>
  );
}

function FieldLabel({ children }) {
  return (
    <span style={fieldLabelStyle}>
      {children}
    </span>
  );
}

function ContextStat({ label, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 11, color: PALETTE.ink3, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, color: PALETTE.ink, fontWeight: 650, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{value}</div>
    </div>
  );
}

function Notice({ title, body, tone = 'neutral', compact = false }) {
  const warn = tone === 'warn';
  return (
    <div style={{
      padding: compact ? 12 : 16,
      background: warn ? PALETTE.warnSoft : PALETTE.surfaceAlt,
      border: `1px solid ${warn ? '#EBDCB2' : PALETTE.lineSoft}`,
      borderRadius: 10,
    }}>
      <div style={{ color: PALETTE.ink, fontWeight: 650, fontSize: 13, marginBottom: 4 }}>{title}</div>
      <div style={{ color: PALETTE.ink2, fontSize: 12.5, lineHeight: 1.45 }}>{body}</div>
    </div>
  );
}

function Toast({ toast }) {
  const color = toast.tone === 'ok'
    ? PALETTE.success
    : toast.tone === 'err'
      ? PALETTE.danger
      : PALETTE.ink3;
  return <span style={{ color, fontSize: 12.5, lineHeight: 1.35 }}>{toast.message}</span>;
}

function PageShell({ children }) {
  return (
    <div style={{
      padding: 'clamp(18px, 4vw, 38px) clamp(16px, 4vw, 38px) 64px',
      maxWidth: 1320,
      margin: '0 auto',
      fontFamily: 'Inter, sans-serif',
      color: PALETTE.ink,
    }}>
      {children}
    </div>
  );
}

const headerStyle = {
  display: 'flex',
  alignItems: 'end',
  justifyContent: 'space-between',
  gap: 18,
  marginBottom: 24,
  flexWrap: 'wrap',
};

const eyebrowStyle = {
  fontSize: 11,
  letterSpacing: 0,
  textTransform: 'uppercase',
  color: PALETTE.ink3,
  marginBottom: 6,
};

const titleStyle = {
  fontFamily: 'Instrument Serif, serif',
  fontStyle: 'italic',
  fontSize: 'clamp(34px, 5vw, 48px)',
  fontWeight: 400,
  margin: 0,
  color: PALETTE.ink,
  letterSpacing: 0,
};

const subtitleStyle = {
  marginTop: 8,
  color: PALETTE.ink2,
  fontSize: 13.5,
};

const summaryGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
  gap: 12,
  marginBottom: 18,
};

const topGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
  gap: 16,
  marginBottom: 26,
};

const lowerGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
  gap: 16,
  marginTop: 22,
};

const defaultsGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 360px), 1fr))',
  gap: 16,
};

const defaultsGroupStyle = {
  minWidth: 0,
  background: PALETTE.surfaceAlt,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 10,
  padding: 14,
};

const managedMediaStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  minHeight: 72,
  padding: '13px 14px',
  background: PALETTE.surface,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 8,
};

const metricStyle = {
  background: PALETTE.surface,
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 12,
  padding: '15px 16px',
  display: 'grid',
  gap: 8,
  minWidth: 0,
};

const metricLabelStyle = {
  fontSize: 11,
  color: PALETTE.ink3,
  textTransform: 'uppercase',
  letterSpacing: 0,
};

const panelStyle = {
  borderRadius: 12,
  minWidth: 0,
};

const panelTextStyle = {
  margin: 0,
  color: PALETTE.ink2,
  fontSize: 12.5,
  lineHeight: 1.45,
};

const providerHeaderStyle = {
  padding: 18,
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
  display: 'flex',
  justifyContent: 'space-between',
  gap: 12,
  flexWrap: 'wrap',
};

const providerTitleStyle = {
  margin: 0,
  color: PALETTE.ink,
  fontSize: 16,
  fontWeight: 650,
  letterSpacing: 0,
};

const providerBodyStyle = {
  padding: 18,
};

const connectionIdentityGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 190px), 1fr))',
  gap: 8,
};

const connectionIdentityItemStyle = {
  display: 'grid',
  gap: 6,
  background: PALETTE.surfaceAlt,
  border: `1px solid ${PALETTE.lineSoft}`,
  borderRadius: 8,
  padding: '11px 12px',
  minWidth: 0,
};

const connectionIdentityValueStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: PALETTE.ink,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

const emptyMaskedStyle = {
  marginTop: 14,
  color: PALETTE.ink3,
  fontSize: 12,
  padding: '9px 10px',
  borderRadius: 8,
  background: PALETTE.surfaceAlt,
  border: `1px dashed ${PALETTE.line}`,
};

const formGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
  gap: 12,
};

const inputStyle = {
  width: '100%',
  padding: '10px 12px',
  fontSize: 13,
  fontFamily: 'Inter, sans-serif',
  color: PALETTE.ink,
  background: PALETTE.surface,
  border: `1px solid ${PALETTE.line}`,
  borderRadius: 8,
  outline: 'none',
  boxSizing: 'border-box',
};

const selectStyle = {
  ...inputStyle,
  cursor: 'pointer',
};

const actionsStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  flexWrap: 'wrap',
};

const fieldLabelStyle = {
  fontSize: 11,
  letterSpacing: 0,
  textTransform: 'uppercase',
  color: PALETTE.ink3,
  fontWeight: 700,
};

const contextGridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
  gap: 12,
};

const sectionHeaderStyle = {
  display: 'flex',
  alignItems: 'end',
  justifyContent: 'space-between',
  gap: 14,
  margin: '8px 0 14px',
  flexWrap: 'wrap',
};

const sectionTitleStyle = {
  margin: 0,
  color: PALETTE.ink,
  fontSize: 18,
  fontWeight: 650,
};

const sectionBodyStyle = {
  margin: '5px 0 0',
  color: PALETTE.ink2,
  fontSize: 13,
  lineHeight: 1.45,
};

const panelHeaderRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 12,
  marginBottom: 16,
};

const memberRowStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 10,
  padding: '10px 0',
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
};

const limitRowStyle = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto',
  gap: 12,
  alignItems: 'start',
  padding: '10px 0',
  borderBottom: `1px solid ${PALETTE.lineSoft}`,
};
